/**
 * Emergent Marketplace — Phase 5
 * VendorStorefront.jsx
 * Architectural Layer: Presentation → Marketplace
 *
 * The public vendor page. Accessible at /market/{slug}
 * Shows: vendor hero, bio, products grid, reviews, booking CTA.
 * Design: Sacred editorial — rich plum, antique gold, warm linen.
 * Font: EB Garamond (display) + Manrope (body)
 */

import { useState, useEffect } from "react";
import {
  Star, Package, Download, BookOpen, Calendar,
  ShoppingBag, ChevronRight, Check, Globe, MapPin,
  ArrowLeft, Share2, Layers
} from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import { apiFetch } from "./api";
import { toast } from "sonner";
import { AddedToCartOverlay } from "../../components/marketplace/AddedToCartOverlay";
import StorefrontDealsBlock from "./StorefrontDealsBlock";
import ReturnPolicyView from "@/components/marketplace/ReturnPolicyView";
import { cssUrl } from "@/lib/cssUrl";

// ─── Styles ──────────────────────────────────────────────────────────────────
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=EB+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&family=Manrope:wght@300;400;500;600;700&display=swap');

  .vs-root {
    --plum:        #2a1a3a;
    --plum-mid:    #3d2a54;
    --gold:        #c0900a;
    --gold-light:  #e0b030;
    --gold-bg:     #fdf8e8;
    --linen:       #faf6ee;
    --linen-dark:  #f0e8d8;
    --white:       #ffffff;
    --border:      #e4d8c8;
    --ink:         #1a1018;
    --ink-mid:     #5a4858;
    --ink-light:   #9a8898;
    --teal:        #1a5a44;
    --teal-bg:     #eaf5f0;
    --teal-border: #a0d8c4;
    font-family: 'Manrope', sans-serif;
    background: var(--linen);
    color: var(--ink);
    min-height: 100vh;
  }

  /* ── Nav bar ─────────────────────────────────────────────────────────── */
  .vs-nav {
    background: var(--white);
    border-bottom: 1px solid var(--border);
    padding: 14px 24px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: sticky;
    top: 0;
    z-index: 30;
  }

  .vs-back-btn {
    display: flex; align-items: center; gap: 7px;
    background: none; border: none; cursor: pointer;
    font-family: 'Manrope', sans-serif;
    font-size: 13px; font-weight: 600;
    color: var(--ink-mid);
    transition: color 0.15s;
  }

  .vs-back-btn:hover { color: var(--plum); }

  .vs-share-btn {
    display: flex; align-items: center; gap: 6px;
    padding: 7px 14px;
    border-radius: 8px;
    border: 1px solid var(--border);
    background: none;
    font-family: 'Manrope', sans-serif;
    font-size: 12px; font-weight: 600;
    color: var(--ink-mid);
    cursor: pointer;
    transition: border-color 0.15s;
  }

  .vs-share-btn:hover { border-color: var(--plum); color: var(--plum); }

  /* ── Hero banner ─────────────────────────────────────────────────────── */
  .vs-banner {
    /* Exactly the 4:1 the adjuster frames, at every width — what the vendor framed is what
       shows. aspect-ratio with min/max-height was worse than the old fixed height: with no
       width set the browser kept the ratio by changing the WIDTH, leaving a gap at 1920 and
       overflowing the card on a phone. */
    width: 100%;
    aspect-ratio: 4 / 1;
    background: linear-gradient(135deg, var(--plum) 0%, var(--plum-mid) 60%, #5a3a70 100%);
    position: relative;
    overflow: hidden;
  }

  .vs-banner::after {
    content: '';
    position: absolute;
    bottom: 0; left: 0; right: 0;
    height: 80px;
    background: linear-gradient(to bottom, transparent, rgba(42,26,58,0.3));
  }

  /* Decorative orb */
  .vs-banner::before {
    content: '';
    position: absolute;
    top: -40px; right: -40px;
    width: 200px; height: 200px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(192,144,10,0.15) 0%, transparent 70%);
  }

  /* ── Profile section ─────────────────────────────────────────────────── */
  .vs-profile {
    padding: 0 24px 20px;
    position: relative;
  }

  .vs-avatar-wrap {
    position: relative;
    display: flex;
    justify-content: center;
    margin-top: -44px;
    margin-bottom: 12px;
  }

  .vs-avatar {
    width: 96px; height: 96px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--gold), var(--plum-mid));
    border: 4px solid var(--white);
    display: flex; align-items: center; justify-content: center;
    font-family: 'EB Garamond', serif;
    font-size: 40px; font-weight: 600;
    color: white;
    box-shadow: 0 4px 16px rgba(0,0,0,0.15);
    overflow: hidden;
    object-fit: cover;
  }

  .vs-store-name {
    font-family: 'EB Garamond', serif;
    font-size: 26px;
    font-weight: 500;
    color: var(--ink);
    margin: 0 0 5px;
  }

  .vs-profile-meta {
    display: flex;
    gap: 14px;
    align-items: center;
    flex-wrap: wrap;
    margin-bottom: 12px;
    font-size: 13px;
    color: var(--ink-light);
  }

  .vs-rating {
    display: flex; align-items: center; gap: 5px;
    font-size: 14px; font-weight: 700;
    color: var(--gold);
  }

  .vs-bio {
    font-size: 14px;
    color: var(--ink-mid);
    line-height: 1.7;
    max-width: 600px;
    margin: 0 0 16px;
  }

  /* Category tags */
  .vs-cats {
    display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 16px;
  }

  .vs-cat-tag {
    padding: 4px 12px;
    border-radius: 16px;
    background: var(--linen-dark);
    border: 1px solid var(--border);
    font-size: 12px;
    font-weight: 600;
    color: var(--ink-mid);
    letter-spacing: 0.03em;
  }

  /* CTA row */
  .vs-cta-row { display: flex; gap: 10px; flex-wrap: wrap; }

  .vs-btn-book {
    display: flex; align-items: center; gap: 8px;
    padding: 11px 22px;
    border-radius: 10px;
    background: var(--plum);
    border: none;
    color: white;
    font-family: 'Manrope', sans-serif;
    font-size: 14px; font-weight: 700;
    cursor: pointer;
    transition: background 0.15s;
  }

  .vs-btn-book:hover { background: var(--plum-mid); }

  .vs-btn-contact {
    display: flex; align-items: center; gap: 8px;
    padding: 11px 20px;
    border-radius: 10px;
    border: 1.5px solid var(--border);
    background: none;
    color: var(--ink-mid);
    font-family: 'Manrope', sans-serif;
    font-size: 14px; font-weight: 700;
    cursor: pointer;
    transition: border-color 0.15s;
  }

  .vs-btn-contact:hover { border-color: var(--plum); color: var(--plum); }

  /* ── Section tabs ────────────────────────────────────────────────────── */
  .vs-tabs {
    display: flex;
    padding: 0 24px;
    background: var(--white);
    border-top: 1px solid var(--border);
    border-bottom: 1px solid var(--border);
    position: sticky;
    top: 56px;
    z-index: 20;
    overflow-x: auto;
  }

  .vs-tab {
    padding: 12px 18px;
    border: none; background: none;
    font-family: 'Manrope', sans-serif;
    font-size: 13px; font-weight: 600;
    color: var(--ink-light);
    cursor: pointer;
    border-bottom: 2px solid transparent;
    transition: all 0.15s;
    white-space: nowrap;
    display: flex; align-items: center; gap: 6px;
  }

  .vs-tab.active { color: var(--plum); border-bottom-color: var(--plum); }

  .vs-tab-count {
    background: var(--linen-dark);
    border-radius: 10px;
    padding: 1px 7px;
    font-size: 11px;
  }

  /* ── Product grid ────────────────────────────────────────────────────── */
  .vs-content { padding: 20px 24px 40px; }

  .vs-product-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 14px;
  }

  .vs-product-card {
    background: var(--white);
    border: 1px solid var(--border);
    border-radius: 14px;
    overflow: hidden;
    cursor: pointer;
    transition: box-shadow 0.15s, transform 0.15s;
  }

  .vs-product-card:hover {
    box-shadow: 0 6px 20px rgba(0,0,0,0.09);
    transform: translateY(-2px);
  }

  .vs-product-img {
    height: 130px;
    background: linear-gradient(135deg, var(--linen-dark), #e8e0cc);
    display: flex; align-items: center; justify-content: center;
    color: var(--ink-light);
    position: relative;
  }

  .vs-product-type-badge {
    position: absolute;
    top: 8px; left: 8px;
    padding: 3px 9px;
    border-radius: 10px;
    font-size: 10px; font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  .vs-type-physical { background: #eef4fd; color: #1a50a8; }
  .vs-type-digital  { background: var(--teal-bg); color: var(--teal); }
  .vs-type-course   { background: var(--gold-bg); color: var(--gold); }
  .vs-type-session  { background: #f4eef8; color: #6030a0; }

  .vs-product-body { padding: 12px; }

  .vs-product-name {
    font-family: 'EB Garamond', serif;
    font-size: 15px; font-weight: 500;
    color: var(--ink);
    margin: 0 0 5px;
    line-height: 1.3;
  }

  .vs-product-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .vs-product-price {
    font-family: 'EB Garamond', serif;
    font-size: 17px; font-weight: 600;
    color: var(--plum);
  }

  .vs-product-rating {
    display: flex; align-items: center; gap: 3px;
    font-size: 11px; color: var(--gold);
    font-weight: 700;
  }

  .vs-add-cart-btn {
    width: 100%;
    margin-top: 10px;
    padding: 9px;
    border-radius: 8px;
    background: var(--plum);
    border: none;
    color: white;
    font-family: 'Manrope', sans-serif;
    font-size: 13px; font-weight: 700;
    cursor: pointer;
    display: flex; align-items: center; justify-content: center; gap: 6px;
    transition: background 0.15s;
    opacity: 0;
    transition: opacity 0.15s, background 0.15s;
  }

  .vs-product-card:hover .vs-add-cart-btn { opacity: 1; }
  /* No hover on a phone, so the button was simply invisible there */
  @media (hover: none) {
    .vs-add-cart-btn { opacity: 1; }
  }
  .vs-add-cart-btn:hover { background: var(--plum-mid); }

  /* ── Reviews tab ─────────────────────────────────────────────────────── */
  .vs-review-summary {
    display: flex;
    gap: 20px;
    align-items: center;
    background: var(--white);
    border: 1px solid var(--border);
    border-radius: 14px;
    padding: 20px;
    margin-bottom: 16px;
  }

  .vs-review-avg {
    font-family: 'EB Garamond', serif;
    font-size: 48px; font-weight: 600;
    color: var(--ink);
    line-height: 1;
    text-align: center;
    flex-shrink: 0;
  }

  .vs-review-card {
    background: var(--white);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 16px 18px;
    margin-bottom: 10px;
  }

  .vs-review-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 8px;
  }

  .vs-reviewer-name {
    font-size: 14px; font-weight: 700;
    color: var(--ink);
    margin: 0 0 3px;
  }

  .vs-reviewer-meta { font-size: 12px; color: var(--ink-light); }

  .vs-review-title {
    font-family: 'EB Garamond', serif;
    font-size: 15px; font-weight: 500;
    color: var(--ink); margin: 0 0 5px;
  }

  .vs-review-body {
    font-size: 14px; color: var(--ink-mid);
    line-height: 1.65; margin: 0 0 10px;
  }

  .vs-vendor-reply {
    background: var(--linen-dark);
    border-left: 3px solid var(--gold);
    border-radius: 0 8px 8px 0;
    padding: 10px 14px;
    font-size: 13px; color: var(--ink-mid);
    line-height: 1.55;
  }

  .vs-vendor-reply strong {
    display: block;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--gold);
    margin-bottom: 4px;
  }
`;

// ─── Mock data ────────────────────────────────────────────────────────────────

const MOCK_VENDOR = {
  id: "v1",
  store_name: "Sacred Roots",
  store_slug: "sacred-roots",
  description: "Hand-crafted botanical blends, guided meditations, and spiritual support for the conscious seeker. Each offering is created with deep intention and reverence for the healing journey.",
  categories: ["Spiritual Tools", "Meditation", "Crystals & Minerals"],
  rating_avg: 4.9,
  rating_count: 43,
  has_sessions: true,
};

const MOCK_PRODUCTS = [
  { id: "p1", type: "digital",  title: "Morning Awakening Series",   price: 27,  rating_avg: 5.0, rating_count: 12 },
  { id: "p2", type: "physical", title: "Selenite Charging Bowl",      price: 48,  rating_avg: 4.7, rating_count: 8 },
  { id: "p3", type: "course",   title: "Conscious Living: 21 Days",  price: 197, rating_avg: 4.9, rating_count: 15 },
  { id: "p4", type: "digital",  title: "Sacred Geometry Journal PDF", price: 15,  rating_avg: 4.8, rating_count: 6 },
  { id: "p5", type: "session",  title: "1:1 Clarity Session",         price: 120, rating_avg: 5.0, rating_count: 9 },
];

const MOCK_REVIEWS = [
  {
    id: "r1", customer_name: "Priya N.", rating: 5,
    title: "Profoundly transformative",
    body: "This session shifted something deep in me. Maya's presence was grounding and her questions were exactly what I needed.",
    vendor_reply: "Thank you Priya — this means so much. It was an honour to hold space for you. 🙏",
    created_at: "2026-03-11",
  },
  {
    id: "r2", customer_name: "Lucas D.", rating: 5,
    title: "Worth every penny",
    body: "The Morning Awakening series has genuinely changed my mornings. I've been using it every day for three weeks.",
    vendor_reply: null,
    created_at: "2026-03-08",
  },
  {
    id: "r3", customer_name: "Sofia R.", rating: 4,
    title: "Beautiful crystal, quick shipping",
    body: "The bowl is gorgeous — even more beautiful in person than in the photos. Shipped quickly and well-packaged.",
    vendor_reply: null,
    created_at: "2026-03-05",
  },
];

const TYPE_ICONS = { physical: Package, digital: Download, course: BookOpen, session: Calendar };

// ─── Main Component ───────────────────────────────────────────────────────────

export default function VendorStorefront({ onAddToCart, onBookSession }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [tab, setTab]       = useState(
    typeof window !== "undefined" && new URLSearchParams(window.location.search).get("tab") === "returns"
      ? "returns" : "products"
  );
  const [vendor, setVendor] = useState(null);
  const [returnPolicy, setReturnPolicy] = useState(null);
  const [products, setProducts] = useState([]);
  const [reviews, setReviews]   = useState([]);
  const [collection, setCollection] = useState(null);
  const [collectionMeta, setCollectionMeta] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [justAdded, setJustAdded] = useState(null);

  // App.js mounts this page without an onAddToCart, so the tile's button did nothing at all
  // (testing agent, iteration 199). It now adds on its own and confirms.
  const addPlainProduct = async (p) => {
    if (onAddToCart) return onAddToCart(p);
    try {
      await apiFetch('/cart/items', {
        method: 'POST', body: JSON.stringify({ product_id: p.id, qty: 1 }),
      });
      window.dispatchEvent(new Event('cart-updated'));
      setJustAdded(p);
    } catch (e) {
      toast.error(e.message || 'Could not add that to your basket');
    }
  };

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    apiFetch(`/marketplace/vendors/${slug}`)
      .then(({ vendor: v, products: p }) => { setVendor(v); setProducts(p || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!vendor?.id) return;
    apiFetch(`/reviews/vendor/${vendor.id}`).then(setReviews).catch(() => {});
    apiFetch(`/marketplace/return-policy/${vendor.id}`).then(setReturnPolicy).catch(() => setReturnPolicy(null));
    apiFetch(`/marketplace/collections/${vendor.id}`)
      .then((d) => setCollectionMeta(d.collections || []))
      .catch(() => setCollectionMeta([]));
  }, [vendor?.id]);

  const sessionProducts = products.filter(p => p.type === "session");
  const nonSessionProducts = products.filter(p => p.type !== "session");
  const collections = [...new Set(nonSessionProducts.map(p => p.category).filter(Boolean))].sort();
  const shownProducts = collection
    ? nonSessionProducts.filter(p => p.category === collection)
    : nonSessionProducts;

  if (loading || !vendor) return (
    <div style={{ padding: 40, textAlign: "center", color: "#8b80a8" }}>
      {loading ? "Loading storefront..." : "Vendor not found"}
    </div>
  );

  return (
    <>
      <style>{styles}</style>
      <div className="vs-root">

        {/* Nav */}
        <div className="vs-nav">
          <button className="vs-back-btn" onClick={() => navigate("/dashboard/marketplace")}>
            <ArrowLeft size={16} /> Marketplace
          </button>
          <button className="vs-share-btn">
            <Share2 size={13} /> Share
          </button>
        </div>

        {/* Banner */}
        <div
          className="vs-banner"
          style={vendor.banner_url ? {
            backgroundImage: cssUrl(vendor.banner_url),
            backgroundSize: "cover",
            backgroundPosition: "center",
          } : undefined}
          data-testid="storefront-banner"
        />

        {/* Profile */}
        <div className="vs-profile">
          <div className="vs-avatar-wrap">
            {vendor.avatar_url ? (
              <img className="vs-avatar" src={vendor.avatar_url} alt={vendor.store_name}
                style={{ objectFit: 'cover' }} data-testid="store-logo-img" />
            ) : (
              <div className="vs-avatar" data-testid="store-logo-initial">{vendor.store_name[0]}</div>
            )}
          </div>
          <h1 className="vs-store-name">{vendor.store_name}</h1>
          <div className="vs-profile-meta">
            <div className="vs-rating">
              <Star size={15} fill="currentColor" />
              {vendor.rating_avg.toFixed(1)}
              <span style={{ color: "var(--ink-light)", fontWeight: 400 }}>({vendor.rating_count})</span>
            </div>
            <span>{products.length} products</span>
          </div>
          <p className="vs-bio">{vendor.description}</p>
          <div className="vs-cats">
            {vendor.categories.map(c => (
              <span key={c} className="vs-cat-tag">{c}</span>
            ))}
          </div>
          <div className="vs-cta-row">
            {sessionProducts.length > 0 && (
              <button className="vs-btn-book" onClick={() => onBookSession?.(vendor.id)}>
                <Calendar size={15} /> Book a session
              </button>
            )}
            <button className="vs-btn-contact">
              Message {vendor.store_name.split(" ")[0]}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="vs-tabs">
          <button className={`vs-tab ${tab === "products" ? "active" : ""}`} onClick={() => setTab("products")}>
            Products <span className="vs-tab-count">{nonSessionProducts.length}</span>
          </button>
          {sessionProducts.length > 0 && (
            <button className={`vs-tab ${tab === "sessions" ? "active" : ""}`} onClick={() => setTab("sessions")}>
              Sessions <span className="vs-tab-count">{sessionProducts.length}</span>
            </button>
          )}
          <button className={`vs-tab ${tab === "reviews" ? "active" : ""}`} onClick={() => setTab("reviews")}>
            Reviews <span className="vs-tab-count">{vendor.rating_count}</span>
          </button>
          <button
            className={`vs-tab ${tab === "returns" ? "active" : ""}`}
            onClick={() => setTab("returns")}
            data-testid="storefront-returns-tab"
          >
            Returns
          </button>
        </div>

        {/* Deals & coupon code input — vendor-scoped, and only where buying happens */}
        {tab !== "returns" && <StorefrontDealsBlock vendorId={vendor.id} products={products} />}

        {/* Content */}
        <div className="vs-content">
          {tab === "products" && (
            <>
            {collections.length > 1 && (
              <div style={{ marginBottom: 20 }} data-testid="storefront-collections">
                <p style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase",
                  color: "var(--ink-light)", margin: "0 0 10px" }}>Collections</p>
                <div style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 4 }}>
                  <button type="button" onClick={() => setCollection(null)}
                    data-testid="storefront-collection-all"
                    style={{
                      flex: "0 0 auto", width: 128, border: "none", padding: 0,
                      background: "transparent", cursor: "pointer", textAlign: "left",
                    }}>
                    <span style={{
                      display: "block", width: 128, height: 84, borderRadius: 10,
                      background: "linear-gradient(135deg, rgba(91,181,162,0.35), rgba(196,148,90,0.3))",
                      outline: !collection ? "2px solid var(--gold)" : "1px solid rgba(0,0,0,0.08)",
                    }} />
                    <span style={{ display: "block", marginTop: 6, fontSize: 12,
                      color: !collection ? "var(--ink)" : "var(--ink-light)", fontWeight: 600 }}>
                      Everything
                    </span>
                  </button>

                  {collections.map(c => {
                    const meta = collectionMeta.find(m => m.name === c) || {};
                    const on = collection === c;
                    return (
                      <button key={c} type="button" onClick={() => setCollection(c)}
                        data-testid={`storefront-collection-${c.toLowerCase().replace(/\s+/g, "-")}`}
                        style={{
                          flex: "0 0 auto", width: 128, border: "none", padding: 0,
                          background: "transparent", cursor: "pointer", textAlign: "left",
                        }}>
                        <span style={{
                          display: "block", width: 128, height: 84, borderRadius: 10,
                          backgroundColor: "rgba(91,181,162,0.1)",
                          backgroundImage: cssUrl(meta.cover_url),
                          backgroundSize: "cover", backgroundPosition: "center",
                          outline: on ? "2px solid var(--gold)" : "1px solid rgba(0,0,0,0.08)",
                        }} />
                        <span style={{ display: "block", marginTop: 6, fontSize: 12,
                          color: on ? "var(--ink)" : "var(--ink-light)", fontWeight: 600 }}>
                          {c}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="vs-product-grid">
              {shownProducts.map(p => {
                const Icon = TYPE_ICONS[p.type] || Package;
                return (
                  <div key={p.id} className="vs-product-card" onClick={() => navigate(`/dashboard/marketplace/product/${p.id}`)} style={{ cursor: 'pointer' }}>
                    <div className="vs-product-img">
                      {p.images?.[0] ? (
                        <img src={p.images[0]} alt={p.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <Icon size={30} style={{ opacity: 0.3 }} />
                      )}
                      <span className={`vs-product-type-badge vs-type-${p.type}`}>{p.type}</span>
                    </div>
                    <div className="vs-product-body">
                      <p className="vs-product-name">{p.title}</p>
                      <div className="vs-product-footer">
                        <span className="vs-product-price">${Number(p.price).toFixed(2)}</span>
                        {p.rating_avg > 0 && (
                          <div className="vs-product-rating">
                            <Star size={11} fill="currentColor" />
                            {p.rating_avg.toFixed(1)}
                          </div>
                        )}
                      </div>
                      {(p.variants?.length || 0) > 0 ? (
                        <button className="vs-add-cart-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/dashboard/marketplace/product/${p.id}`);
                          }}
                          data-testid={`vs-choose-type-${p.id}`}>
                          <Layers size={13} /> Choose type
                        </button>
                      ) : (
                        <button className="vs-add-cart-btn"
                          onClick={(e) => { e.stopPropagation(); addPlainProduct(p); }}
                          data-testid={`vs-add-to-cart-${p.id}`}>
                          <ShoppingBag size={13} /> Add to cart
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            </>
          )}

          {tab === "sessions" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {sessionProducts.map(p => (
                <div
                  key={p.id}
                  style={{
                    background: "var(--white)",
                    border: "1px solid var(--border)",
                    borderRadius: 14,
                    padding: 20,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                  onClick={() => onBookSession?.(vendor.id, p.id)}
                >
                  <div>
                    <p style={{ fontFamily: "'EB Garamond', serif", fontSize: 18, fontWeight: 500, margin: "0 0 4px", color: "var(--ink)" }}>{p.title}</p>
                    {p.rating_avg > 0 && (
                      <div className="vs-product-rating" style={{ marginBottom: 0 }}>
                        <Star size={11} fill="currentColor" /> {p.rating_avg.toFixed(1)} · {p.rating_count} reviews
                      </div>
                    )}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <span className="vs-product-price">${p.price}</span>
                    <button className="vs-btn-book" style={{ padding: "8px 16px", fontSize: 13 }}>
                      Book <ChevronRight size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === "reviews" && (
            <>
              <div className="vs-review-summary">
                <div>
                  <div className="vs-review-avg">{vendor.rating_avg.toFixed(1)}</div>
                  <div style={{ display: "flex", gap: 3, justifyContent: "center", margin: "6px 0 4px" }}>
                    {[1,2,3,4,5].map(n => (
                      <Star key={n} size={16}
                        fill={n <= Math.round(vendor.rating_avg) ? "#c0900a" : "none"}
                        color={n <= Math.round(vendor.rating_avg) ? "#c0900a" : "#e4d8c8"}
                      />
                    ))}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--ink-light)", textAlign: "center" }}>{vendor.rating_count} reviews</div>
                </div>
                <div style={{ flex: 1, fontSize: 13, color: "var(--ink-mid)", lineHeight: 1.65 }}>
                  <p style={{ margin: "0 0 4px", fontWeight: 600, color: "var(--ink)" }}>What clients are saying</p>
                  Verified reviews from customers who purchased products or completed sessions with {vendor.store_name}.
                </div>
              </div>

              {reviews.map(r => (
                <div key={r.id} className="vs-review-card">
                  <div className="vs-review-header">
                    <div>
                      <p className="vs-reviewer-name">{r.customer_name}</p>
                      <p className="vs-reviewer-meta">{r.created_at ? new Date(r.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : ""} · Verified purchase</p>
                    </div>
                    <div style={{ display: "flex", gap: 3 }}>
                      {[1,2,3,4,5].map(n => (
                        <Star key={n} size={13}
                          fill={n <= r.rating ? "#c0900a" : "none"}
                          color={n <= r.rating ? "#c0900a" : "#e4d8c8"}
                        />
                      ))}
                    </div>
                  </div>
                  {r.title && <p className="vs-review-title">{r.title}</p>}
                  <p className="vs-review-body">{r.body}</p>
                  {r.vendor_reply && (
                    <div className="vs-vendor-reply">
                      <strong>Response from {vendor.store_name}</strong>
                      {r.vendor_reply}
                    </div>
                  )}
                </div>
              ))}
            </>
          )}

          {tab === "returns" && (
            <div style={{ maxWidth: 720, color: "var(--ink)" }} data-testid="storefront-returns-panel">
              <ReturnPolicyView data={returnPolicy} />
            </div>
          )}
        </div>
      </div>

      {justAdded && (
        <AddedToCartOverlay product={justAdded} onClose={() => setJustAdded(null)} />
      )}
    </>
  );
}
